import React, { useMemo, useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  TextField, 
  Typography, 
  Button, 
  Paper, 
  Divider, 
  useMediaQuery,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UserPageLayout from '../../components/user/UserPageLayout';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRestaurant } from '../../contexts/RestaurantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { orderService } from '../../services/orderService';
import { CheckoutPageSkeleton } from '../../components/common/SkeletonLoader';

const CheckoutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, getCartTotal, clearCart, outlets, selectedOutlet, isDeliveryAvailable } = useRestaurant();
  const { showSuccess } = useNotification();

  const orderType = location.state?.serviceType || location.state?.orderType || 'delivery';
  const userInfo = location.state?.userInfo || JSON.parse(sessionStorage.getItem('userInfo') || '{}');

  const [form, setForm] = useState({
    outlet_id: null, // Will be set dynamically
    customer_name: userInfo.name || '',
    customer_phone: userInfo.phone || '',
    customer_email: userInfo.email || '',
    // Singapore-style address fields
    building_name: '',
    unit_number: '',
    street_address: '',
    postal_code: '',
    delivery_instructions: '',
  });
  const [paymentMethod, setPaymentMethod] = useState('pay_online');
  const [pickupTime, setPickupTime] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  
  // Check if delivery is available for the selected outlet
  const canProceedWithDelivery = orderType === 'delivery' ? isDeliveryAvailable() : true;

  // Set outlet_id when outlets are loaded or when selectedOutlet changes
  useEffect(() => {
    if (selectedOutlet && selectedOutlet.id) {
      setForm(prev => ({ ...prev, outlet_id: selectedOutlet.id }));
    } else if (outlets && outlets.length > 0 && !form.outlet_id) {
      setForm(prev => ({ ...prev, outlet_id: outlets[0].id }));
    }
  }, [outlets, selectedOutlet, form.outlet_id]);

  // Check order status periodically after order creation
  useEffect(() => {
    let interval;
    if (orderCreated && createdOrder && createdOrder.status === 'pending') {
      // TEMPORARILY DISABLED: Order status polling to prevent repeated API calls
      console.log('Order status polling temporarily disabled to prevent repeated API calls');
      /*
      interval = setInterval(async () => {
        try {
          const updatedOrder = await orderService.getOrderById(createdOrder.id);
          if (updatedOrder && updatedOrder.status !== createdOrder.status) {
            setCreatedOrder(updatedOrder);
            if (updatedOrder.status === 'approved') {
              showSuccess('Your order has been approved! You can now proceed to payment.');
            }
          }
        } catch (error) {
          console.error('Error checking order status:', error);
        }
      }, 10000); // Check every 10 seconds
      */
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [orderCreated, createdOrder, showSuccess]);



  const summary = useMemo(() => {
    const subtotal = getCartTotal ? getCartTotal() : 0;
    const delivery_fee = orderType === 'delivery' ? (subtotal > 30 ? 0 : 5) : 0;
    const tax_amount = Math.round(subtotal * 0.09); // Singapore GST 9%
    const total_amount = subtotal + delivery_fee + tax_amount;
    return { subtotal, delivery_fee, tax_amount, total_amount };
  }, [getCartTotal, orderType]);

  const toOrderPayload = () => {
    return {
      outlet_id: form.outlet_id,
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      customer_email: form.customer_email || undefined,
      delivery_address: orderType === 'delivery' ? 
        `${form.unit_number}, ${form.building_name}, ${form.street_address}, Singapore ${form.postal_code}` : 
        undefined,
      delivery_instructions: orderType === 'delivery' ? (form.delivery_instructions || undefined) : undefined,
      items: cart.map(ci => ({
        menu_item_id: ci.id,
        quantity: ci.quantity || 1,
        size_name: ci.selectedSize?.name || null,
        size_price: ci.selectedSize?.priceModifier || 0,
        base_price: ci.base_price || ci.price || 0,
        total_price: (ci.price || ci.base_price || 0) * (ci.quantity || 1),
        special_instructions: ci.special_instructions || undefined,
        addons: (ci.addons || []).map(a => ({ addon_id: a.id, quantity: a.quantity || 1, price: a.price }))
      })),
      subtotal: summary.subtotal,
      tax_amount: summary.tax_amount,
      delivery_fee: summary.delivery_fee,
      discount_amount: cart.reduce((sum, item) => {
        if (item.discount_enabled && item.discount_percentage > 0) {
          const originalTotal = item.original_price * item.quantity;
          const discountedTotal = item.discounted_price * item.quantity;
          return sum + (originalTotal - discountedTotal);
        }
        return sum;
      }, 0),
      total_amount: summary.total_amount,
      special_instructions: undefined,
      payment_method: paymentMethod,
      ...(orderType === 'takeaway' && pickupTime && { pickup_time: pickupTime }),
    };
  };

  const validateForm = () => {
    const errors = [];
    
    if (!cart || cart.length === 0) {
      errors.push('Your cart is empty. Please add items before requesting an order.');
    }
    
    if (!form.outlet_id) {
      errors.push('Please select an outlet for your order.');
    }
    
    if (!form.customer_name.trim()) {
      errors.push('Full name is required');
    }
    
    if (!form.customer_phone.trim()) {
      errors.push('Phone number is required');
    }
    
    // Check delivery availability
    if (orderType === 'delivery' && !isDeliveryAvailable()) {
      errors.push('Delivery service is not available at this outlet. Please choose takeaway or dine-in instead.');
    }
    
    // Check Singapore address fields for delivery orders
    if (orderType === 'delivery' && isDeliveryAvailable()) {
      if (!form.building_name.trim()) {
        errors.push('Building name is required for delivery orders.');
      }
      if (!form.unit_number.trim()) {
        errors.push('Unit number is required for delivery orders.');
      }
      if (!form.street_address.trim()) {
        errors.push('Street address is required for delivery orders.');
      }
      if (!form.postal_code.trim()) {
        errors.push('Postal code is required for delivery orders.');
      }
      // Validate Singapore postal code format (6 digits)
      if (form.postal_code.trim() && !/^\d{6}$/.test(form.postal_code.trim())) {
        errors.push('Please enter a valid Singapore postal code (6 digits).');
      }
    }
    
    return errors;
  };

  const getValidationErrors = () => {
    return validateForm();
  };

  const handleRequestOrder = async () => {
    // Validate required fields
    const errors = validateForm();
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    setSubmitting(true);
    try {
      // Create order based on type
      const orderEndpoint = orderType === 'delivery' ? 'delivery' : 'takeaway';
      const payload = toOrderPayload();
      
      // Debug logging
      console.log('Creating order with payload:', payload);
      console.log('Order endpoint:', orderEndpoint);
      
      const order = await orderService.createOrder(orderEndpoint, payload);
      
      setCreatedOrder(order);
      setOrderCreated(true);
      showSuccess && showSuccess('Order requested successfully! Please proceed with payment.');
    } catch (e) {
      console.error('Order creation error:', e);
      let errorMessage = 'Failed to request order. Please try again.';
      
      if (e.response) {
        // Server responded with error
        if (e.response.status === 403) {
          errorMessage = 'Access denied. Please check your order details and try again.';
        } else if (e.response.status === 400) {
          errorMessage = e.response.data?.detail || 'Invalid order data. Please check your details.';
        } else if (e.response.status === 422) {
          errorMessage = 'Validation error. Please check your order details.';
        } else {
          errorMessage = `Server error (${e.response.status}). Please try again.`;
        }
      } else if (e.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Validate required fields first
    const errors = validateForm();
    if (errors.length > 0) {
      alert('Please fix the following errors:\n' + errors.join('\n'));
      return;
    }

    setSubmitting(true);
    try {
      // Create order
      const orderEndpoint = orderType === 'delivery' ? 'delivery' : 'takeaway';
      const payload = toOrderPayload();
      
      console.log('Creating order with payload:', JSON.stringify(payload, null, 2));
      console.log('Order endpoint:', orderEndpoint);
      console.log('Cart items:', cart);
      
      const order = await orderService.createOrder(orderEndpoint, payload);
      setCreatedOrder(order);
      setOrderCreated(true);

      if (paymentMethod === 'pay_online') {
        // Process online payment with HitPay
        try {
          const hp = await orderService.createHitpayPayment(order.id);
          if (hp?.checkout_url) {
            // Store order details in sessionStorage for payment result page
            sessionStorage.setItem('pendingOrder', JSON.stringify({
              id: order.id,
              order_number: order.order_number,
              total_amount: order.total_amount,
              order_type: orderType,
              customer_name: form.customer_name,
              customer_phone: form.customer_phone,
              customer_email: form.customer_email
            }));
            window.location.href = hp.checkout_url;
          } else {
            // Payment creation failed
            navigate('/payment/failed', { 
              state: { 
                status: 'failed', 
                order: {
                  id: order.id,
                  order_number: order.order_number,
                  total_amount: order.total_amount,
                  order_type: orderType,
                  customer_name: form.customer_name,
                  customer_phone: form.customer_phone,
                  customer_email: form.customer_email
                }
              } 
            });
          }
        } catch (error) {
          console.error('HitPay payment creation failed:', error);
          navigate('/payment/failed', { 
            state: { 
              status: 'failed', 
              order: {
                id: order.id,
                order_number: order.order_number,
                total_amount: order.total_amount,
                order_type: orderType,
                customer_name: form.customer_name,
                customer_phone: form.customer_phone,
                customer_email: form.customer_email
              }
            } 
          });
        }
      } else if (paymentMethod === 'cash_on_delivery') {
        // For cash on delivery, confirm payment and then redirect to success page
        try {
          const paymentData = {
            provider_payment_id: `cash_${order.id}_${Date.now()}`,
            payment_method: 'cash_on_delivery',
            amount: order.total_amount,
            currency: 'SGD',
            payment_metadata: {
              order_id: order.id,
              order_number: order.order_number,
              payment_type: orderType,
              confirmed_at: new Date().toISOString()
            }
          };
          
          await orderService.confirmPayment(order.id, paymentData);
          console.log('Cash payment confirmed successfully');
          
          clearCart && clearCart();
          navigate('/payment/success', { 
            state: { 
              status: 'success', 
              order: {
                id: order.id,
                order_number: order.order_number,
                total_amount: order.total_amount,
                order_type: orderType,
                payment_method: 'cash_on_delivery'
              }
            } 
          });
        } catch (error) {
          console.error('Failed to confirm cash payment:', error);
          // Still redirect to success page even if confirmation fails
          clearCart && clearCart();
          navigate('/payment/success', { 
            state: { 
              status: 'success', 
              order: {
                id: order.id,
                order_number: order.order_number,
                total_amount: order.total_amount,
                order_type: orderType,
                payment_method: 'cash_on_delivery'
              }
            } 
          });
        }
      } else if (['visa', 'mastercard', 'american_express', 'discover'].includes(paymentMethod)) {
        // For card payments (dummy), show coming soon message
        showSuccess('Card payment is coming soon! Please use "Pay Online (HitPay)" or "Cash on ' + (orderType === 'delivery' ? 'Delivery' : 'Pickup') + '" for now.');
        setSubmitting(false);
        return;
      }
    } catch (e) {
      console.error('Order creation/payment error:', e);
      let errorMessage = 'Failed to process order. Please try again.';
      
      if (e.response) {
        // Server responded with error
        if (e.response.status === 422) {
          errorMessage = 'Validation error. Please check your order details and try again.';
        } else if (e.response.status === 400) {
          errorMessage = e.response.data?.detail || 'Invalid order data. Please check your details.';
        } else if (e.response.status === 403) {
          errorMessage = 'Access denied. Please check your order details and try again.';
        } else {
          errorMessage = `Server error (${e.response.status}). Please try again.`;
        }
      } else if (e.request) {
        // Network error
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Show skeleton loader while loading outlets or submitting
  if (!outlets || outlets.length === 0 || submitting) {
    return (
      <UserPageLayout title="Checkout" subtitle={`Confirm ${orderType} details and pay securely`}>
        <CheckoutPageSkeleton />
      </UserPageLayout>
    );
  }

  return (
    <UserPageLayout title="Checkout" subtitle={`Confirm ${orderType} details and pay securely`}>
      <Box sx={{ mt: 2 }}>
        {/* Empty Cart Warning */}
        {(!cart || cart.length === 0) && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={600}>
              🛒 Your cart is empty
            </Typography>
            <Typography variant="body2">
              You need to add items to your cart before you can checkout. 
              <Button
                variant="text"
                size="small"
                onClick={() => navigate('/' + (orderType === 'delivery' ? 'delivery' : 'takeaway'))}
                sx={{ ml: 1, textTransform: 'none' }}
              >
                Browse Menu
              </Button>
            </Typography>
          </Alert>
        )}

        <Grid container spacing={isMobile ? 2 : 3}>
          <Grid item xs={12} md={7}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                {orderType === 'delivery' ? 'Delivery Details' : 'Pickup Details'}
              </Typography>
              
              {/* Order Summary */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Order Summary
                </Typography>
                {cart && cart.length > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {cart.length} items • {orderType === 'delivery' ? 'Delivery' : 'Takeaway'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total: S${summary.total_amount.toFixed(2)}
                    </Typography>
                  </>
                ) : (
                  <Alert severity="info" sx={{ py: 0 }}>
                    <Typography variant="body2">
                      No items in cart. Please add items from the menu.
                    </Typography>
                  </Alert>
                )}
              </Box>

              {/* Delivery Unavailable Warning */}
              {orderType === 'delivery' && !isDeliveryAvailable() && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight={600}>
                    ⚠️ Delivery Service Unavailable
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery service is currently unavailable at {selectedOutlet?.name}. 
                    Please choose takeaway or dine-in instead.
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/checkout', { state: { orderType: 'takeaway' } })}
                    >
                      Switch to Takeaway
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => navigate('/table-reservations')}
                    >
                      Switch to Dine-in
                    </Button>
                  </Box>
                </Alert>
              )}

              {/* User Information */}
              {userInfo && (userInfo.name || userInfo.phone) && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.main">
                    👤 {userInfo.isGuest ? 'Guest User' : 'Registered User'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userInfo.name} • {userInfo.phone}
                  </Typography>
                  {userInfo.email && (
                    <Typography variant="caption" color="text.secondary">
                      {userInfo.email}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Outlet Information */}
              {selectedOutlet && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary.main">
                    📍 Selected Outlet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedOutlet.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedOutlet.address}, {selectedOutlet.city}, {selectedOutlet.state}
                  </Typography>
                </Box>
              )}
              
              {/* Outlet Loading/Error State */}
              {!outlets || outlets.length === 0 ? (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="warning.main">
                    ⚠️ Loading Outlets...
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please wait while we load available outlets for your order.
                  </Typography>
                </Box>
              ) : !form.outlet_id ? (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'error.50', borderRadius: 1, border: '1px solid', borderColor: 'error.200' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom color="error.main">
                    ❌ No Outlet Selected
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Please select an outlet to proceed with your order.
                  </Typography>
                </Box>
              ) : null}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Full Name *" 
                    value={form.customer_name} 
                    onChange={e => setForm({ ...form, customer_name: e.target.value })}
                    required
                    error={!form.customer_name.trim() && orderCreated === false}
                    helperText={!form.customer_name.trim() && orderCreated === false ? 'Full name is required' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Phone *" 
                    value={form.customer_phone} 
                    onChange={e => setForm({ ...form, customer_phone: e.target.value })}
                    required
                    error={!form.customer_phone.trim() && orderCreated === false}
                    helperText={!form.customer_phone.trim() && orderCreated === false ? 'Phone number is required' : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Email (optional)" 
                    value={form.customer_email} 
                    onChange={e => setForm({ ...form, customer_email: e.target.value })} 
                  />
                </Grid>

                {orderType === 'delivery' && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: 'primary.main' }}>
                        📍 Singapore Delivery Address
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="Building Name *" 
                        value={form.building_name} 
                        onChange={e => setForm({ ...form, building_name: e.target.value })}
                        required
                        disabled={!isDeliveryAvailable()}
                        error={!form.building_name.trim() && orderCreated === false}
                        helperText={!form.building_name.trim() && orderCreated === false ? 'Building name is required' : ''}
                        placeholder="e.g., Marina Bay Sands"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField 
                        fullWidth 
                        label="Unit Number *" 
                        value={form.unit_number} 
                        onChange={e => setForm({ ...form, unit_number: e.target.value })}
                        required
                        disabled={!isDeliveryAvailable()}
                        error={!form.unit_number.trim() && orderCreated === false}
                        helperText={!form.unit_number.trim() && orderCreated === false ? 'Unit number is required' : ''}
                        placeholder="e.g., #12-34"
                      />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField 
                        fullWidth 
                        label="Street Address *" 
                        value={form.street_address} 
                        onChange={e => setForm({ ...form, street_address: e.target.value })}
                        required
                        disabled={!isDeliveryAvailable()}
                        error={!form.street_address.trim() && orderCreated === false}
                        helperText={!form.street_address.trim() && orderCreated === false ? 'Street address is required' : ''}
                        placeholder="e.g., 10 Bayfront Avenue"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField 
                        fullWidth 
                        label="Postal Code *" 
                        value={form.postal_code} 
                        onChange={e => setForm({ ...form, postal_code: e.target.value })}
                        required
                        disabled={!isDeliveryAvailable()}
                        error={(!form.postal_code.trim() || !/^\d{6}$/.test(form.postal_code.trim())) && orderCreated === false}
                        helperText={
                          !form.postal_code.trim() && orderCreated === false 
                            ? 'Postal code is required' 
                            : form.postal_code.trim() && !/^\d{6}$/.test(form.postal_code.trim()) && orderCreated === false
                              ? 'Enter 6-digit Singapore postal code'
                              : ''
                        }
                        placeholder="018956"
                        inputProps={{ maxLength: 6 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField 
                        fullWidth 
                        multiline 
                        minRows={2} 
                        label="Delivery Instructions (optional)" 
                        value={form.delivery_instructions} 
                        onChange={e => setForm({ ...form, delivery_instructions: e.target.value })}
                        disabled={!isDeliveryAvailable()}
                        placeholder="e.g., Leave at door, call when arrived"
                      />
                    </Grid>
                    {orderType === 'takeaway' && (
                      <Grid item xs={12}>
                        <TextField 
                          fullWidth 
                          type="datetime-local"
                          label="Preferred Pickup Time" 
                          value={pickupTime} 
                          onChange={e => setPickupTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          inputProps={{
                            min: new Date().toISOString().slice(0, 16)
                          }}
                          helperText="Select when you'd like to pick up your order"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12}>
                      <Box sx={{ 
                        p: 2, 
                        bgcolor: 'info.50', 
                        borderRadius: 2, 
                        border: '1px solid', 
                        borderColor: 'info.200' 
                      }}>
                        <Typography variant="body2" color="info.main" sx={{ fontWeight: 600, mb: 1 }}>
                          📍 Your Complete Address:
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {form.unit_number && form.building_name && form.street_address && form.postal_code
                            ? `${form.unit_number}, ${form.building_name}, ${form.street_address}, Singapore ${form.postal_code}`
                            : 'Please fill in all address fields to see your complete address'
                          }
                        </Typography>
                      </Box>
                    </Grid>
                  </>
                )}
                {orderType === 'takeaway' && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      You will receive pickup instructions after order confirmation.
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Payment Method Selection */}
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Payment Method</Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                >
                  <FormControlLabel 
                    value="pay_online" 
                    control={<Radio />} 
                    label="Pay Online (HitPay)" 
                  />
                  <FormControlLabel 
                    value="visa" 
                    control={<Radio />} 
                    label="💳 Visa (Coming Soon)" 
                  />
                  <FormControlLabel 
                    value="mastercard" 
                    control={<Radio />} 
                    label="💳 Mastercard (Coming Soon)" 
                  />
                  <FormControlLabel 
                    value="american_express" 
                    control={<Radio />} 
                    label="💳 American Express (Coming Soon)" 
                  />
                  <FormControlLabel 
                    value="discover" 
                    control={<Radio />} 
                    label="💳 Discover (Coming Soon)" 
                  />
                  <FormControlLabel 
                    value="cash_on_delivery" 
                    control={<Radio />} 
                    label={orderType === 'delivery' ? 'Cash on Delivery' : 'Cash on Pickup'} 
                  />
                </RadioGroup>
              </FormControl>
              {paymentMethod === 'cash_on_delivery' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    {orderType === 'delivery' 
                      ? 'You will pay in cash when your order is delivered.'
                      : 'You will pay in cash when you pick up your order.'
                    }
                  </Typography>
                </Alert>
              )}
              {['visa', 'mastercard', 'american_express', 'discover'].includes(paymentMethod) && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    This payment method is coming soon! Please select "Pay Online (HitPay)" or "Cash on {orderType === 'delivery' ? 'Delivery' : 'Pickup'}" for now.
                  </Typography>
                </Alert>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>Order Summary</Typography>
              {orderCreated && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    ✅ Order #{createdOrder?.order_number} Requested Successfully!
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                    {createdOrder?.status === 'approved' 
                      ? 'Your order has been approved! You can now proceed with payment.'
                      : 'Your order has been submitted and is waiting for admin approval. You will be notified when it\'s approved.'
                    }
                  </Typography>
                  {createdOrder?.status === 'pending' && (
                    <>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        💡 What happens next? The restaurant will review your order and approve it. You'll be able to pay once approved.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          try {
                            const updatedOrder = await orderService.getOrderById(createdOrder.id);
                            setCreatedOrder(updatedOrder);
                            if (updatedOrder.status === 'approved') {
                              showSuccess('Your order has been approved! You can now proceed to payment.');
                            }
                          } catch (error) {
                            console.error('Error refreshing order status:', error);
                          }
                        }}
                        sx={{ mr: 1 }}
                      >
                        Check Status
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/' + (orderType === 'delivery' ? 'delivery' : 'takeaway'))}
                      >
                        Continue Shopping
                      </Button>
                    </>
                  )}
                </Alert>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Subtotal</Typography>
                <Typography>S${summary.subtotal.toFixed(2)}</Typography>
              </Box>
              {orderType === 'delivery' && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>Delivery Fee</Typography>
                  <Typography>{summary.delivery_fee === 0 ? 'FREE' : `S$${summary.delivery_fee.toFixed(2)}`}</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Taxes</Typography>
                <Typography>S${summary.tax_amount.toFixed(2)}</Typography>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={800}>To Pay</Typography>
                <Typography variant="h6" fontWeight={800} color="primary.main">S${summary.total_amount.toFixed(2)}</Typography>
              </Box>
              {!orderCreated ? (
                <>
                  {(!cart || cart.length === 0) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        Your cart is empty. Please add items from the menu before requesting an order.
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate('/' + (orderType === 'delivery' ? 'delivery' : 'takeaway'))}
                        sx={{ mt: 1 }}
                      >
                        Go to Menu
                      </Button>
                    </Alert>
                  )}
                  <Button 
                    disabled={submitting} 
                    onClick={handlePlaceOrder} 
                    fullWidth 
                    size="large" 
                    variant="contained" 
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                  >
                    {submitting ? 'Processing...' : 
                      (!cart || cart.length === 0) ? 'Cart is Empty' : 
                      paymentMethod === 'pay_online' ? 'Place Order & Pay Online' : 
                      ['visa', 'mastercard', 'american_express', 'discover'].includes(paymentMethod) ? 'Coming Soon - Select Different Payment' :
                      `Place Order (${orderType === 'delivery' ? 'Cash on Delivery' : 'Cash on Pickup'})`
                    }
                  </Button>
                </>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600}>
                      🎉 Order #{createdOrder?.order_number} has been requested successfully!
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Your order is now in the restaurant's queue for review.
                    </Typography>
                  </Alert>
                  
                  {/* Show payment button only after admin approval */}
                  {createdOrder?.status === 'approved' ? (
                    <>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          ✅ Your order has been approved!
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          You can now proceed with payment to complete your order.
                        </Typography>
                      </Alert>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                        <Button 
                          disabled={submitting} 
                          onClick={handlePlaceOrder} 
                          fullWidth 
                          size="large" 
                          variant="contained" 
                          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                        >
                          {submitting ? 'Processing...' : (
                            paymentMethod === 'pay_online' ? 'Pay with HitPay' : 
                            ['visa', 'mastercard', 'american_express', 'discover'].includes(paymentMethod) ? 'Coming Soon - Select Different Payment' :
                            `Complete ${orderType} Order (Cash)`
                          )}
                        </Button>
                      </Box>
                    </>
                  ) : (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        ⏳ Waiting for admin approval. You will be notified when your order is approved and ready for payment.
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        💡 What happens next? The restaurant will review your order and approve it. You'll be able to pay once approved.
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={async () => {
                          try {
                            const updatedOrder = await orderService.getOrderById(createdOrder.id);
                            setCreatedOrder(updatedOrder);
                            if (updatedOrder.status === 'approved') {
                              showSuccess('Your order has been approved! You can now proceed to payment.');
                            }
                          } catch (error) {
                            console.error('Error refreshing order status:', error);
                          }
                        }}
                        sx={{ mr: 1 }}
                      >
                        Check Status
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate('/' + (orderType === 'delivery' ? 'delivery' : 'takeaway'))}
                      >
                        Continue Shopping
                      </Button>
                    </Alert>
                  )}
                  
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Button 
                      disabled={submitting} 
                      onClick={() => {
                        if (window.confirm('Are you sure you want to modify your order? This will cancel the current request and you\'ll need to submit a new one.')) {
                          setOrderCreated(false);
                          setCreatedOrder(null);
                        }
                      }} 
                      size="large" 
                      variant="outlined" 
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                      Modify Order
                    </Button>
                  </Box>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </UserPageLayout>
  );
};

export default CheckoutPage;


